import { useEffect, useState } from 'react'

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/frontend/components/ui/input-group'
import { cn } from '@/frontend/lib/utils'

interface CurrencyInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  /** Value in minor currency units (e.g. pence, cents). */
  value: number | null
  /** Called with the new value in minor currency units, or null if cleared/invalid. */
  onChange: (value: number | null) => void
  /** Currency symbol shown as a prefix. Defaults to "£". */
  symbol?: string
}

/**
 * Currency input that displays and accepts values in minor currency units (e.g. pence, cents).
 *
 * Maintains a draft string while the user types, parsing and emitting on blur.
 * Blur also tidies the display (e.g. "35" → "35.00", garbage → cleared).
 * Built on InputGroup so the currency symbol sits inside the input border.
 */
export function CurrencyInput({
  value,
  onChange,
  onBlur,
  symbol = '£',
  className,
  ...props
}: CurrencyInputProps) {
  const [draft, setDraft] = useState(() => formatDisplay(value))

  // Sync draft when external value changes (e.g. form reset)
  useEffect(() => {
    setDraft(formatDisplay(value))
  }, [value])

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let parsed: number | null
    try {
      parsed = parseCurrency(draft)
    } catch {
      parsed = null
    }
    onChange(parsed)
    setDraft(formatDisplay(parsed))
    onBlur?.(e)
  }

  return (
    <InputGroup className={cn('w-full', className)}>
      <InputGroupAddon>
        <InputGroupText>{symbol}</InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        {...props}
      />
    </InputGroup>
  )
}

/** Formats a minor-unit value for display (e.g. 3500 → "35.00"). */
function formatDisplay(value: number | null): string {
  if (value == null) return ''
  return (value / 100).toFixed(2)
}

/** Parses a currency string to a number. Returns null if empty. Throws on invalid input. */
function parseCurrency(str: string): number | null {
  const trimmed = str.trim()
  if (trimmed === '') return null
  const num = Number.parseFloat(trimmed)
  if (Number.isNaN(num)) throw new Error(`Invalid currency value: "${trimmed}"`)
  return Math.round(num * 100)
}
